import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import * as moment from "moment";
import { Button, Divider, Form, Label, Modal } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { toast } from "@teamprevent/common/toasts";
import {
    useNotInThePastValidator,
    useTimeHasAlreadyPassed,
    useValidateStartBeforeEndDate,
    useValidateStartEndDateSameDay
} from "@teamprevent/common/customHooks";

import { Link } from "@teamprevent/common/task/taskActions/models";

import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";

import { OrgStructureDetailModel } from "../../../crud/orgStructure";

import {
    ALL_CUSTOMERS_VALUE,
    BillabilityType,
    BillableByType,
    BillableByTypeOptions,
    billableCustomerBillingTypes,
    ClinicDetails,
    ClinicLocationType,
    EditClinicModel,
    shouldShowBillableCustomerField } from "../models";

import { editClinic } from "../actions";
import { PartialCustomerListModel } from "../../../crud/customer";
import { ApplicationState } from "../../models";
import { RoomSelector } from "../../components/RoomSelector";

import { BillableOrgStructure1Dropdown } from "./BillableOrgStructure1Dropdown";

interface Props {
    clinic: ClinicDetails;
    customers: PartialCustomerListModel[];
}

export const EditClinicButton: React.FunctionComponent<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const notInThePastValidator = useNotInThePastValidator();

    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);

    const [newStartTime, setNewStartTime] = React.useState<moment.Moment>(props.clinic?.start);
    const [isNewStartTimeValid, setIsNewStartTimeValid] = React.useState<boolean>(true);

    const [newEndTime, setNewEndtime] = React.useState<moment.Moment>(props.clinic?.end);
    const [isNewEndTimeValid, setIsNewEndTimeValid] = React.useState<boolean>(true);

    const [billableByType, setBillableByType] = React.useState<string>(props.clinic?.billableByType);
    const [billableCustomers, setBillableCustomers] = React.useState<string[]>(props.clinic?.billableCustomerIds);
    const billableCustomer = billableCustomers?.length > 0 ? billableCustomers[0] : undefined;

    const [isOrgStructureBillingValid, setIsOrgStructureBillingValid] = React.useState<boolean>(false);
    const [billableOrgStructure, setBillableOrgStructure] = React.useState<Link | undefined>(props.clinic?.billableOrgStructure);

    const [selectedClinicLocationTypes, setSelectedClinicLocationTypes] = React.useState<ClinicLocationType[]>([]);
    const [selectedRoom, setSelectedRoom] = React.useState<{ id: string; name: string} | null>(null);

    const defaultClinicLocationTypes = useSelector((state: ApplicationState) => state.clinicLocationTypes);

    const startTimeHasAlreadyPassed = useTimeHasAlreadyPassed(props.clinic?.start);
    const endTimeHasAlreadyPassed = useTimeHasAlreadyPassed(props.clinic?.end);

    const billableCustomerOptions = React.useMemo(() => {
        const isAllCustomersClinic = props.clinic?.customerIds?.includes(ALL_CUSTOMERS_VALUE.id);
        const emptyCustomers = !props.clinic?.customerIds || props.clinic.customerIds?.length === 0;
        if (isAllCustomersClinic || emptyCustomers) {
            return props.customers.map(customer => ({ key: customer.id, value: customer.id, text: customer.name }));
        }

        return props.clinic?.customerIds?.map(id => {
            const customerName = props.customers.find(c => c.id === id)?.name;
            return ({
                key: id,
                value: id,
                text: customerName
            });
        });
    }, [props]);

    const isAMultiple = (date1: moment.Moment, date2: moment.Moment, slotLengthInMinutes: number) => {
        return (date1.diff(date2, "minutes") % slotLengthInMinutes) === 0;
    };

    const isSlotMultiple = (model: EditClinicModel) => {
        const slotLengthInMinutes = props.clinic.slotLengthInMinutes;
        if (slotLengthInMinutes) {
            const isStartMultiple = isAMultiple(model.start, props.clinic.start, slotLengthInMinutes);
            if (!isStartMultiple) {
                toast.info(`New start time must be some multiple of ${props.clinic.slotLengthInMinutes} minutes`);
            }

            const isEndMultiple = isAMultiple(model.end, props.clinic.end, slotLengthInMinutes);
            if (!isEndMultiple) {
                toast.info(`New end time must be some multiple of ${props.clinic.slotLengthInMinutes} minutes`);
            }

            return isStartMultiple && isEndMultiple;
        }

        return true;
    };

    const isAnyBookingOutsideNewTimes = (model: EditClinicModel) => {
        const childAppointments = props.clinic.appointments;
        if (!childAppointments || childAppointments.length === 0) {
            return false;
        }

        const startBookingTimes = childAppointments.map(ca => ca.start);
        const minStartBookingTime = moment.min(startBookingTimes);

        const endBookingTimes = childAppointments.map(ca => ca.end);
        const maxEndBookingTime = moment.max(endBookingTimes);

        const isStartTimeOverlapping = model.start > minStartBookingTime;
        const isEndTimeOverlapping = model.end < maxEndBookingTime;

        const isOverlapping = isStartTimeOverlapping || isEndTimeOverlapping;
        if (isOverlapping) {
            toast.info("New start or end time overlaps with already booked appointments");
            return true;
        }

        return false;
    };

    const calendarHighlight = React.useCallback(() => {
        if (newStartTime && newEndTime && newStartTime.isValid() && newEndTime.isValid()) {
            return { start: newStartTime, end: newEndTime };
        }

        return undefined;
    }, [newEndTime, newStartTime]);

    const onEdit = () => setIsEditModalOpen(true);

    const onCancel = () => setIsEditModalOpen(false);

    const resetForm = () => {
        setSelectedClinicLocationTypes([]);
        setSelectedRoom(null);
        setSubmitted(false);
    };

    const onSave = () => {
        setSubmitted(true);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const invalidStartOrEndTime = !useValidateStartBeforeEndDate(newStartTime, newEndTime) || !useValidateStartEndDateSameDay(newStartTime, newEndTime);

        if (invalidStartOrEndTime) {
            return;
        }

        if (!validateBillableFields())
        {
            return;
        }

        if (!roomSelectionValid()) {
            return;
        }

        const updatedClinicModel: EditClinicModel = {
            start: newStartTime ?? props.clinic.start,
            end: newEndTime ?? props.clinic.end,
            billableByType: billableByType ?? props.clinic.billableByType,
            billableCustomerIds: billableCustomers,
            billableOrgStructure,
            clinicLocationTypes: selectedClinicLocationTypes,
            roomId: selectedRoom?.id
        };

        if (!isSlotMultiple(updatedClinicModel)) {
            return;
        }

        if (isAnyBookingOutsideNewTimes(updatedClinicModel)) {
            return;
        }

        dispatch(editClinic(props.clinic.id, updatedClinicModel));
        setIsEditModalOpen(false);
        resetForm();
    };

    const validateBillableFields = (): boolean => {
        const mustHaveBillableCustomer = billableCustomerBillingTypes.includes(billableByType as BillabilityType);

        if (mustHaveBillableCustomer) {
            if (!billableCustomer) {
                return false;
            }
            else if (!isOrgStructureBillingValid) {
                return false;
            }
        }

        return true;
    };

    const roomRequired = React.useCallback(() => {
        const anyClinicTypesRequireRoom = selectedClinicLocationTypes.find(l => l.requiresRoom);
        const alreadyHasRoom = props.clinic.rooms && props.clinic.rooms.length > 0;
        return anyClinicTypesRequireRoom !== undefined && !alreadyHasRoom ;
    }, [props.clinic.rooms, selectedClinicLocationTypes]);

    const roomSelectionValid = React.useCallback(() => {
        if (!roomRequired())
        {
            return true;
        }

        return selectedRoom !== null;
    }, [roomRequired, selectedRoom]);

    const onStartTimeChange = (value: moment.Moment, isValid: boolean) => {
        if (!isValid) {
            setIsNewStartTimeValid(false);
        }
        setNewStartTime(value && value.utc(true));
    };

    const onEndTimeChange = (value: moment.Moment, isValid: boolean) => {
        if (!isValid) {
            setIsNewEndTimeValid(false);
        }
        setNewEndtime(value && value.utc(true));
    };

    const onBillableByTypeChange = (value: string) => {
        setBillableByType(value);
        if (value === BillabilityType.BillableByTask) {
            setBillableCustomers([]);
        }
    };

    const onBillableCustomerChange = (value: string) => {
        if (!value) {
            setBillableCustomers([]);
        } else {
            setBillableCustomers([value]);
        }
    };

    const onBillableOrgStructureChange = React.useCallback((orgStructure: OrgStructureDetailModel | undefined, isValid: boolean) => {
        setIsOrgStructureBillingValid(isValid);
        setBillableOrgStructure(orgStructure);
    }, []);

    const showBillableCustomerField = React.useCallback(() => {
        return shouldShowBillableCustomerField({ name: billableByType } as BillableByType);
    }, [billableByType]);

    const onClinicLocationTypesChange = React.useCallback((value) => {
        const selectedLocationTypes = defaultClinicLocationTypes.filter(l => value.indexOf(l.name) !== -1);
        setSelectedClinicLocationTypes(selectedLocationTypes);
    }, [defaultClinicLocationTypes]);

    return (
        <>
            <Modal open={isEditModalOpen}>
                <Modal.Header content="Edit Clinic" />
                <Modal.Content>
                    <Form>
                        <Input.Datetime
                            inline
                            label="Start Time"
                            onChange={onStartTimeChange}
                            value={props.clinic?.start}
                            showErrors={submitted || !isNewStartTimeValid}
                            disabled={startTimeHasAlreadyPassed}
                        />
                        {!useValidateStartBeforeEndDate(newStartTime, newEndTime) &&
                            <Label basic color="red" pointing content="The start time must be before the end" />}
                        <Divider hidden />
                        <Input.Datetime
                            inline
                            label="End Time"
                            onChange={onEndTimeChange}
                            value={props.clinic?.end}
                            validation={[notInThePastValidator]}
                            showErrors={submitted || !isNewEndTimeValid}
                        />
                        {!useValidateStartEndDateSameDay(newStartTime, newEndTime) &&
                            <Label basic color="red" pointing content="The start time must be the same day as the end" />}
                        <Divider hidden />
                        <Input.Dropdown
                            inline
                            inputLabelPosition="left"
                            label="Billable By"
                            value={billableByType}
                            options={BillableByTypeOptions.map(bbt => ({ key: bbt.name, value: bbt.name, text: bbt.name }))}
                            onChange={onBillableByTypeChange}
                            showErrors={submitted}
                        />
                        <>
                            {showBillableCustomerField() && <>
                                <Divider hidden />
                                <Input.Dropdown
                                    required
                                    dynamicOptions
                                    label="Billable Customer"
                                    value={billableCustomer}
                                    showErrors={submitted}
                                    options={billableCustomerOptions}
                                    onChange={onBillableCustomerChange}
                                    search
                                />
                            </>}
                            {billableCustomer && (<BillableOrgStructure1Dropdown
                                billableCustomerId={billableCustomer}
                                value={billableOrgStructure?.id}
                                showErrors={submitted}
                                onChange={onBillableOrgStructureChange}
                            />)}
                        </>
                        <Input.DropdownMulti
                            dynamicOptions
                            label="Clinic Types"
                            value={selectedClinicLocationTypes.map(l => l.name)}
                            showErrors={submitted}
                            options={defaultClinicLocationTypes.filter(l => l.name !== ClinicLocationTypeName.mobile &&
                                !props.clinic.clinicLocationTypes.includes(l.name)).map(clinicLocationType =>
                                ({ key: clinicLocationType.name, value: clinicLocationType.name, text: clinicLocationType.name }))}
                            onChange={onClinicLocationTypesChange}
                        />
                        { roomRequired() &&
                            <RoomSelector
                                selectedRoomName={selectedRoom?.name}
                                highlight={calendarHighlight()}
                                onRoomSelected={(id, name) => {
                                    setSelectedRoom({ id, name });
                                }}
                                onRoomDeselected={() => {
                                    setSelectedRoom(null);
                                }}
                            />
                        }
                        { submitted && !roomSelectionValid() &&
                            <Label basic color="red" pointing content="A room must be selected" />
                        }
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onCancel} />
                    <Button content="Save" onClick={onSave} />
                </Modal.Actions>
            </Modal>
            <Button content="Edit" onClick={onEdit} disabled={endTimeHasAlreadyPassed} />
        </>
    );
};
